import type {KeyboardEvent, ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {Icon} from '@Components/icon-v2';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {AiTextPromptBoxLoading} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-loading';
import {AiTextPromptBoxError} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-error';
import {IconSize} from '@Components/icon-v2/icon.types';
import {AiPromptBoxVariant} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import styles from './ai-text-prompt-box-textfield.module.scss';

interface AiTextPromptBoxTextfieldProps {
  isLoading: boolean;
  onSubmitClick: VoidFunction;
  value: InputType;
  onChange: (value: string) => void;
  isInMobileMode?: boolean;
  errorMessage?: string | null;
  placeholder?: string;
  promptBoxVariant?: AiPromptBoxVariant;
  className?: string;
}

export function AiTextPromptBoxTextfield({placeholder = 'pmwjs_write_your_prompt', ...props}: AiTextPromptBoxTextfieldProps): ReactElement<any> {
  const isError = !!props.errorMessage;
  const promptAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (promptAreaRef.current) {
      promptAreaRef.current.focus();
      promptAreaRef.current.setSelectionRange(promptAreaRef.current.value.length, promptAreaRef.current.value.length);
    }
  }, []);

  const onPromptChangeKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      props.onSubmitClick();
    }
  };

  const getIcon = (): ReactElement<any> | null => {
    if (props.isInMobileMode) {
      return null;
    }

    if (props.isLoading) {
      return <CircularProgressLoader color={LOADER_COLOR.DARK} size={LOADER_SIZE.SMALL} classes={styles.spinner} />;
    }

    return (
      <Icon
        icon="icon-chat-send"
        size={IconSize.SIZE_ICON_16}
        isUsedAsButton
        className={styles.inputIcon}
        onClick={(event) => {
          event.stopPropagation();
          props.onSubmitClick();
        }}
      />
    );
  };

  const loadingInMobileMode = props.isInMobileMode && props.isLoading;
  const errorInMobileMode = props.isInMobileMode && isError && !props.isLoading;

  if (loadingInMobileMode) {
    return (
      <div className={`${styles.infoAreaMobile} flex-row-justify-center flex-row-align-center`}>
        <AiTextPromptBoxLoading isShown message={window.i18next.t('pmwjs_thinking_of_ideas')} />
      </div>
    );
  }

  if (errorInMobileMode) {
    return (
      <div className={`${styles.infoAreaMobile} flex-row-justify-center flex-row-align-center ${styles.error}`}>
        <AiTextPromptBoxError errorMessage={props.errorMessage ?? ''} isError={isError} invertedColors containerClasses="_unmargin" />
      </div>
    );
  }

  return (
    <div className={`${styles.promptAreaContainer} ${props.className ?? ''}`}>
      <textarea
        ref={promptAreaRef}
        onKeyDown={onPromptChangeKeyDown}
        placeholder={placeholder}
        className={`${styles.mobilePromptArea} _full-width ${props.isInMobileMode ? styles.inMobile : ''} ${props.isLoading ? styles.isLoading : ''} ${props.promptBoxVariant === AiPromptBoxVariant.MINIATURE ? styles.templateContentEditorClasses : ''}`}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        value={props.value}
      />
      {getIcon()}
    </div>
  );
}
